<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useAuthStore } from '../stores/auth';
import { Fingerprint } from 'lucide-vue-next';

const loginId = ref('');
const password = ref('');
const authStore = useAuthStore();
const isLoading = ref(false);
const errorMsg = ref('');

const lastEmail = localStorage.getItem('last_login_email');
const lastName = localStorage.getItem('last_login_name');
const hasWebAuthn = localStorage.getItem('has_webauthn') === 'true';
const showPasswordInput = ref(!(lastEmail && hasWebAuthn));

onMounted(() => {
    if (lastEmail) {
        loginId.value = lastEmail;
    }
});

const switchToPassword = () => {
    showPasswordInput.value = true;
};

const handleLogin = async () => {
  isLoading.value = true;
  errorMsg.value = '';
  try {
    await authStore.login(loginId.value, password.value);
  } catch (e: any) {
    errorMsg.value = 'Bismillah, mohon periksa kembali Email/NIP dan Kata Sandi Anda.';
  } finally {
    isLoading.value = false;
  }
};

const handleLoginWebAuthn = async () => {
    const emailToUse = loginId.value || lastEmail;

    if (!emailToUse) {
        showPasswordInput.value = true;
        errorMsg.value = 'Mohon masukkan Email/NIP terlebih dahulu.';
        return;
    }
    
    isLoading.value = true;
    errorMsg.value = '';
    try {
        await authStore.loginWebAuthn(emailToUse);
    } catch (e: any) {
        errorMsg.value = 'Qadarullah, verifikasi gagal: ' + (e.response?.data?.message || 'Pastikan akun terdaftar');
    } finally {
        isLoading.value = false;
    }
};
</script>

<template>
  <div class="min-h-screen w-full flex flex-col items-center justify-center bg-white dark:bg-slate-900 text-gray-900 dark:text-white px-6 pb-safe transition-colors duration-300">
    <div class="w-full max-w-sm mx-auto flex flex-col">
      <!-- Header Section -->
      <div class="mb-12 text-center">
        <div class="inline-flex items-center justify-center w-20 h-20 mb-6 bg-primary/5 dark:bg-primary/20 rounded-3xl rotate-3 shadow-sm">
            <img src="/logo-new.png" alt="MyLAZ Logo" class="w-12 h-12 object-contain drop-shadow-sm -rotate-3" />
        </div>
        <h1 class="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white mb-3">Assalamu'alaikum</h1>
        <p v-if="showPasswordInput || !lastEmail" class="text-gray-500 dark:text-slate-400 font-medium text-lg">Silakan masuk untuk memulai khidmah</p>
      </div>

      <!-- Quick Fingerprint Login Section -->
      <div v-if="lastEmail && !showPasswordInput" class="flex flex-col items-center animate-fade-in-up">
        <div class="relative mb-8 group cursor-pointer" @click="handleLoginWebAuthn">
            <div class="absolute inset-0 bg-primary/20 dark:bg-primary/30 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500 opacity-70"></div>
            <div class="relative w-32 h-32 bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-full flex items-center justify-center border border-primary/20 backdrop-blur-sm group-hover:scale-105 transition-transform duration-300">
                <Fingerprint class="w-16 h-16 text-primary dark:text-primary-400 group-hover:text-primary-600 dark:group-hover:text-primary-300 transition-colors" />
            </div>
            <div class="absolute -bottom-2 -right-2 bg-white dark:bg-slate-800 rounded-full p-2 shadow-lg border border-gray-100 dark:border-slate-700">
                 <div class="animate-pulse w-3 h-3 bg-green-500 rounded-full"></div>
            </div>
        </div>
        
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">Ahlan wa Sahlan, {{ lastName || 'Sahabat' }}</h2>
        <p class="text-gray-500 dark:text-slate-400 text-center mb-8">Sentuh sidik jari untuk melanjutkan</p>

        <div v-if="errorMsg" class="w-full mb-6 p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-600 dark:text-red-400 text-sm rounded-r-lg">
          {{ errorMsg }}
        </div>

        <button 
           type="button"
           @click="handleLoginWebAuthn"
           :disabled="isLoading"
           class="w-full py-4 bg-primary hover:bg-primary-600 active:bg-primary-700 text-white rounded-2xl font-bold text-lg shadow-xl shadow-primary/20 transition-all transform active:scale-[0.98] mb-6 flex items-center justify-center gap-2"
        >
             <span v-if="isLoading">Verifikasi...</span>
             <span v-else>Masuk dengan Sidik Jari</span>
        </button>

        <button @click="switchToPassword" class="text-sm font-semibold text-gray-500 hover:text-gray-900 dark:text-slate-500 dark:hover:text-white transition-colors">
            Gunakan Kata Sandi
        </button>
      </div>

      <!-- Standard Form Section -->
      <form v-else @submit.prevent="handleLogin" class="flex flex-col gap-6 animate-fade-in-up">
        <!-- Email Input -->
        <div class="group">
          <label for="login_id" class="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 ml-1">Email / NIP</label>
          <div class="relative transition-all duration-300">
              <input
                id="login_id"
                v-model="loginId"
                type="text"
                required
                class="block w-full px-5 py-4 bg-gray-50 dark:bg-slate-800/50 border-2 border-transparent focus:border-primary/50 focus:bg-white dark:focus:bg-slate-800 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 outline-none transition-all font-medium"
                placeholder="nama@lazsidogiri.org"
              />
              <!-- Optional icon could go here -->
          </div>
        </div>

        <!-- Password Input -->
        <div class="group">
          <div class="flex items-center justify-between mb-2 ml-1">
             <label for="password" class="block text-sm font-semibold text-gray-700 dark:text-slate-300">Kata Sandi</label>
             <a href="#" @click.prevent="$router.push('/forgot-password')" class="text-sm text-primary font-bold hover:text-primary-600 transition-colors">Lupa?</a>
          </div>
          <input
            id="password"
            v-model="password"
            type="password"
            required
            class="block w-full px-5 py-4 bg-gray-50 dark:bg-slate-800/50 border-2 border-transparent focus:border-primary/50 focus:bg-white dark:focus:bg-slate-800 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 outline-none transition-all font-medium"
            placeholder="••••••••"
          />
        </div>

        <div v-if="errorMsg" class="p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-600 dark:text-red-400 text-sm rounded-r-lg">
          {{ errorMsg }}
        </div>

        <!-- Main Action Button -->
        <button
          type="submit"
          :disabled="isLoading"
          class="w-full mt-4 py-4 bg-primary hover:bg-primary-600 active:bg-primary-700 text-white rounded-2xl font-bold text-lg shadow-xl shadow-primary/20 transition-all transform active:scale-[0.98] flex items-center justify-center"
        >
          <span v-if="isLoading">Proses Masuk...</span>
          <span v-else>Masuk</span>
        </button>

        <!-- Divider -->
        <div class="relative flex py-2 items-center">
            <div class="flex-grow border-t border-gray-200 dark:border-slate-700"></div>
            <span class="flex-shrink-0 mx-4 text-gray-400 text-xs font-bold uppercase tracking-wider">Atau gunakan</span>
            <div class="flex-grow border-t border-gray-200 dark:border-slate-700"></div>
        </div>

        <!-- Secondary Action (Biometric Toggle) -->
        <button 
           v-if="lastEmail"
           type="button"
           @click="showPasswordInput = false"
           class="w-full py-3.5 bg-white dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 rounded-2xl font-bold text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 hover:border-gray-200 transition-all flex items-center justify-center gap-2"
        >
             <Fingerprint class="w-5 h-5 text-primary" />
             Login Cepat (Sidik Jari)
        </button>
        <button 
           v-else
           type="button"
           @click="handleLoginWebAuthn"
           class="w-full py-3.5 bg-white dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 rounded-2xl font-bold text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 hover:border-gray-200 transition-all flex items-center justify-center gap-2"
        >
             <Fingerprint class="w-5 h-5 text-primary" />
             Login Biometrik
        </button>
      </form>
      
      <!-- Footer Info -->
      <p class="mt-12 text-center text-xs text-gray-400 dark:text-slate-600">
        &copy; 2026 LAZ Sidogiri. All rights reserved.
      </p>
    </div>
  </div>
</template>
